import { useEffect, useMemo, useState } from "react";
import { type SortingState, type Updater } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { Plus, ScrollText } from "lucide-react";
import { type Dispatch, type UnknownAction } from "redux";

import {
  listTermsAndConditionsText as listTermsAndConditionsTextAction,
  listTermsAndConditionsTextReset as listTermsAndConditionsTextResetAction,
} from "@/actions";
import { PageBreadcrumb } from "@/components/PageBreadcrumb";
import { WFPButton } from "@/components/ui/button";
import { getCountryObjectFromCache } from "@/utils";

import { DEFAULT_SORTING, List } from "./components/List";
import { ConnectedTermsAndConditionsTextEditModal } from "./components/Modal";
import { ViewModal } from "./components/ViewModal";
import { type TermsAndConditionsModalOptions, type TermsAndConditionsText } from "./types";

interface TermsAndConditionsListState {
  data: { results?: TermsAndConditionsText[] };
  errorMessage: string;
  isFetching: boolean;
}

interface RootState {
  listTermsAndConditionsText: TermsAndConditionsListState;
}

interface StateProps {
  errorMessage: string;
  isFetching: boolean;
  termsAndConditionsTexts: TermsAndConditionsText[];
}

interface DispatchProps {
  listTermsAndConditionsText: () => void;
  listTermsAndConditionsTextReset: () => void;
}

type WfpManagerTermsAndConditionsTextProps = StateProps & DispatchProps;
type AppDispatch = Dispatch<UnknownAction>;

const CLOSED_MODAL_OPTIONS: TermsAndConditionsModalOptions = {
  id: undefined,
  isOpen: false,
  type: undefined,
};

const dispatchAction = (dispatch: AppDispatch, action: unknown) =>
  dispatch(action as UnknownAction);

export const WfpManagerTermsAndConditionsText = ({
  errorMessage,
  isFetching,
  termsAndConditionsTexts,
  listTermsAndConditionsText,
  listTermsAndConditionsTextReset,
}: WfpManagerTermsAndConditionsTextProps) => {
  const { t } = useTranslation();
  const countryName = getCountryObjectFromCache().name;
  const [modalOptions, setModalOptions] =
    useState<TermsAndConditionsModalOptions>(CLOSED_MODAL_OPTIONS);
  const [sorting, setSorting] = useState<SortingState>(DEFAULT_SORTING);
  const isViewOpen = modalOptions.isOpen && modalOptions.type === "view";
  const selectedRecord = useMemo(
    () => termsAndConditionsTexts.find(record => String(record.id) === String(modalOptions.id)),
    [modalOptions.id, termsAndConditionsTexts]
  );

  useEffect(() => {
    listTermsAndConditionsText();

    return () => {
      listTermsAndConditionsTextReset();
    };
  }, [listTermsAndConditionsText, listTermsAndConditionsTextReset]);

  const onClickAdd = () => setModalOptions({ id: "add", isOpen: true, type: "create" });

  const onSortingChange = (updater: Updater<SortingState>) => {
    setSorting(currentSorting =>
      typeof updater === "function" ? updater(currentSorting) : updater
    );
  };

  return (
    <main className="flex flex-col gap-6 py-4 px-6 sm:py-6 sm:px-8 md:py-8 md:px-10">
      <div className="flex flex-col gap-4">
        <PageBreadcrumb
          items={[
            { label: t("header.configuration"), href: "/wfpManager/configuration" },
            { label: t("configuration.item.termsAndConditionsText") },
          ]}
        />
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-y-1">
            <div className="flex items-center gap-x-2">
              <span className="inline-flex shrink-0 items-center justify-center size-10 rounded-lg p-2 bg-blue-100 text-blue-600">
                <ScrollText />
              </span>
              <h1 className="font-semibold text-2xl">
                {t("termsAndConditionsText.pageTitle", { country: countryName })}
              </h1>
            </div>
            <p className="whitespace-pre-line text-muted-foreground">
              {t("termsAndConditionsText.subtitle")}
            </p>
          </div>
          <WFPButton type="button" onClick={onClickAdd}>
            <Plus className="size-4" aria-hidden="true" />
            {t("termsAndConditionsText.addButton")}
          </WFPButton>
        </div>
      </div>
      <List
        errorMessage={errorMessage}
        isFetching={isFetching}
        onSortingChange={onSortingChange}
        setModalOptions={setModalOptions}
        sorting={sorting}
        termsAndConditionsTexts={termsAndConditionsTexts}
      />
      <ViewModal
        isOpen={isViewOpen}
        record={selectedRecord}
        onClose={() => setModalOptions(CLOSED_MODAL_OPTIONS)}
        onEdit={() => setModalOptions({ id: modalOptions.id, isOpen: true, type: "edit" })}
      />
      <ConnectedTermsAndConditionsTextEditModal
        modalOptions={isViewOpen ? CLOSED_MODAL_OPTIONS : modalOptions}
        record={selectedRecord}
        setModalOptions={setModalOptions}
      />
    </main>
  );
};

export const mapStateToProps = (state: RootState): StateProps => {
  return {
    termsAndConditionsTexts: state.listTermsAndConditionsText.data.results ?? [],
    isFetching: state.listTermsAndConditionsText.isFetching,
    errorMessage: state.listTermsAndConditionsText.errorMessage,
  };
};

export const mapDispatchToProps = (dispatch: AppDispatch): DispatchProps => {
  return {
    listTermsAndConditionsText: () => dispatchAction(dispatch, listTermsAndConditionsTextAction()),
    listTermsAndConditionsTextReset: () =>
      dispatchAction(dispatch, listTermsAndConditionsTextResetAction()),
  };
};

export const ConnectedWfpManagerTermsAndConditionsText = connect(
  mapStateToProps,
  mapDispatchToProps
)(WfpManagerTermsAndConditionsText);
